import { useMemo, useRef, useState } from 'react';

import { ArrowLeftIcon, ArrowRightIcon } from '@chakra-ui/icons';
import {
  Box,
  BoxProps,
  Flex,
  IconButton,
  Menu,
  MenuList,
  Spacer,
  useColorModeValue,
} from '@chakra-ui/react';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';

import { useAuth } from '@r-client/shared/feature/auth';

import { getAccessedNavMeta } from '../navigation-meta';
import { ProfileMenu } from '../profile-menu/profile-menu';
import { SideBarHeader } from './side-bar-header';
import { SideMenuItem } from './side-menu-item';
import { SubMenuList } from './sub-menu-list/sub-menu-list';

interface ISidebarProps extends BoxProps {
  onClose?: () => void;
  collapsed?: boolean;
  handleCollapse?: () => void;
  appLastUpdated?: string;
  appReleaseVersion?: string;
}

export const SidebarContent = observer(function SidebarContent({
  onClose,
  collapsed,
  handleCollapse,
  appLastUpdated,
  appReleaseVersion,
  ...rest
}: ISidebarProps) {
  const router = useRouter();
  const { viewer } = useAuth();
  const accessMenuList = useMemo(() => {
    const userCapabilities = viewer?.info?.capabilities || [];
    return getAccessedNavMeta(userCapabilities);
  }, [viewer]);
  const pos = useRef(
    accessMenuList.findIndex((m) =>
      m.subItem?.some((sm) => sm.path === router.pathname)
    )
  );
  const [subNavPos, setSubNavPos] = useState<undefined | number>(pos.current);
  const handleNavClick = (path?: string, navPos?: number) => {
    if (path) {
      router.push(path);
      onClose?.();
      if (navPos !== subNavPos) {
        setSubNavPos(undefined);
      }
    } else {
      setSubNavPos(navPos !== subNavPos ? navPos : undefined);
    }
  };

  const handleCollapseMenu = () => {
    if (handleCollapse) {
      setSubNavPos(undefined);
      handleCollapse();
    }
  };

  return (
    <Flex
      direction="column"
      alignItems="center"
      borderRight="1px"
      borderRightColor={useColorModeValue('gray.200', 'gray.700')}
      w={collapsed ? { md: 14 } : { base: 'full', md: 56 }}
      pos="relative"
      left={0}
      h="full"
      px={3}
      fontSize="12px"
      {...rest}
    >
      <SideBarHeader
        onClose={onClose}
        collapsed={collapsed}
        appLastUpdated={appLastUpdated}
        appReleaseVersion={appReleaseVersion}
      />
      {accessMenuList.map((link, navPos) => (
        <Menu key={link.name} autoSelect={false}>
          <SideMenuItem
            link={link}
            currentPath={router.pathname}
            collapsed={collapsed}
            navPos={navPos}
            subNavPos={subNavPos}
            handleNavClick={handleNavClick}
          />
          {link?.subItem?.length &&
            (collapsed ? (
              <MenuList p="0" zIndex={1} m="-6px 0px 0px 32px">
                <SubMenuList
                  isActive={subNavPos === navPos}
                  currentPath={router.pathname}
                  navPos={navPos}
                  subNavItems={link.subItem}
                  handleClick={handleNavClick}
                />
              </MenuList>
            ) : (
              <Box hidden={subNavPos !== navPos} pl={8} w="full">
                <SubMenuList
                  isActive={subNavPos === navPos}
                  currentPath={router.pathname}
                  navPos={navPos}
                  subNavItems={link.subItem}
                  handleClick={handleNavClick}
                />
              </Box>
            ))}
        </Menu>
      ))}
      <Spacer />
      <Flex
        direction={collapsed ? 'column' : 'row'}
        gap={collapsed ? 4 : 0}
        wrap="wrap"
        justifyContent={collapsed ? 'center' : 'flex-end'}
        alignItems="center"
        w={collapsed ? undefined : 'full'}
        mb={3}
      >
        {collapsed && (
          <ProfileMenu
            appLastUpdated={appLastUpdated}
            appReleaseVersion={appReleaseVersion}
          />
        )}
        <IconButton
          aria-label="Collapse side menu"
          icon={
            collapsed ? (
              <ArrowRightIcon boxSize={4} />
            ) : (
              <ArrowLeftIcon boxSize={4} />
            )
          }
          display={{ base: 'none', md: 'initial' }}
          onClick={handleCollapseMenu}
          variant="auxiliary"
          color="blackAlpha.500"
        />
      </Flex>
    </Flex>
  );
});
